import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Plus, Edit2, Trash2, Users, Save, X, Check } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function UserRolesPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user-roles/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Permissões carregadas:', response.data.data);
      setPermissions(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      console.error('Response:', error.response);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        display_name: role.display_name,
        description: role.description,
        permissions: role.permissions || []
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        display_name: '',
        description: '',
        permissions: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      permissions: []
    });
  };

  const handleTogglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const handleSelectAllInCategory = (category) => {
    const categoryPerms = permissions[category].map(p => p.id);
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));

    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');

      if (editingRole) {
        // Atualizar
        await axios.put(
          `${API_URL}/api/user-roles/${editingRole.name}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Perfil atualizado com sucesso!');
      } else {
        // Criar
        await axios.post(
          `${API_URL}/api/user-roles`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Perfil criado com sucesso!');
      }

      handleCloseModal();
      loadRoles();
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      alert(error.response?.data?.error || 'Erro ao salvar perfil');
    }
  };

  const handleDelete = async (roleName) => {
    if (!confirm(`Tem certeza que deseja excluir o perfil "${roleName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/user-roles/${roleName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Perfil excluído com sucesso!');
      loadRoles();
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      alert(error.response?.data?.error || 'Erro ao excluir perfil');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Carregando perfis...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            Gerenciamento de Perfis
          </h1>
          <p className="text-gray-600 mt-1">
            Crie e gerencie perfis de usuário com permissões personalizadas
          </p>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Novo Perfil
        </button>
      </div>

      {/* Grid de Perfis */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map(role => (
          <div
            key={role.name}
            className="bg-white rounded-lg shadow-md p-6 border-l-4"
            style={{
              borderLeftColor: role.is_system ? '#3B82F6' : '#10B981'
            }}
          >
            {/* Header do Card */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  {role.display_name}
                </h3>
                {role.is_system && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mt-1 inline-block">
                    Sistema
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleOpenModal(role)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  title="Editar permissões"
                >
                  <Edit2 className="w-4 h-4" />
                </button>

                {!role.is_system && (
                  <button
                    onClick={() => handleDelete(role.name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Excluir perfil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Descrição */}
            <p className="text-gray-600 text-sm mb-4 min-h-[40px]">
              {role.description || 'Sem descrição'}
            </p>

            {/* Stats */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2 text-gray-700">
                <Users className="w-4 h-4" />
                <span className="text-sm">{role.user_count} usuário(s)</span>
              </div>

              <div className="flex items-center gap-2 text-gray-700">
                <Shield className="w-4 h-4" />
                <span className="text-sm">{role.permissions.length} permissões</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                {editingRole ? `Editar Perfil: ${editingRole.display_name}` : 'Novo Perfil'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Informações Básicas */}
              {!editingRole?.is_system && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Informações Básicas</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Perfil *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={editingRole}
                      className="w-full px-4 py-2 border rounded-lg disabled:bg-gray-100"
                      placeholder="ex: vendedor"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Identificador único (minúsculas, sem espaços)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome de Exibição *
                    </label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="ex: Vendedor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows="3"
                      placeholder="Descreva as responsabilidades deste perfil"
                    />
                  </div>
                </div>
              )}

              {/* Permissões */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Permissões ({formData.permissions.length} selecionadas)
                </h3>

                {Object.keys(permissions).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Carregando permissões...
                  </div>
                ) : null}

                {Object.keys(permissions).map(category => (
                  <div key={category} className="border rounded-lg p-4">
                    {/* Header da Categoria */}
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-800">{category}</h4>
                      <button
                        onClick={() => handleSelectAllInCategory(category)}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        {permissions[category].every(p => formData.permissions.includes(p.id))
                          ? 'Desmarcar Todos'
                          : 'Marcar Todos'}
                      </button>
                    </div>

                    {/* Permissões */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {permissions[category].map(perm => (
                        <label
                          key={perm.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm.id)}
                            onChange={() => handleTogglePermission(perm.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t p-6 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserRolesPage;
